/**
 * OrderbookFreshness — producer-observation age, not browser receipt age.
 *
 * Reads getSourceAge().bookAgeMs, tracked separately from market/trade frames.
 * That separation matters: hydra's Kalshi orderbook-delta delivery can freeze a
 * book while the market-frame price keeps ticking, so a freshness signal built
 * on the general feed clock would lie.
 *
 * Shows a plain rolling age ("0s", "1s", "2s", ...) with NO live/stale coloring.
 * A Kalshi book can legitimately go quiet for several seconds, so flagging that
 * as "not live" is misleading — the data is genuinely current as of N seconds
 * ago. The number is the honest signal; the trader reads it against game
 * context. The only distinct state is "paused" — a confirmed server-side
 * suspension (hydra backpressure level >= 2), which is not a guess.
 *
 * The shared 1s display clock does not subscribe every badge to every market
 * frame. It never changes feed cadence or execution eligibility. Missing clock
 * evidence stays unknown rather than falling back to a misleading receipt age.
 */

import { useMarketStore } from '@/stores/marketStore';
import { useNow } from '@/lib/infra/sharedClock';

export function OrderbookFreshness({ ticker }: { ticker?: string | null }) {
  const paused = useMarketStore((s) => s.backpressureLevel >= 2);
  const now = useNow();

  if (!ticker) return null;
  void now; // and on each 1s tick, so the age climbs with no frames arriving

  const ageMs = useMarketStore.getState().getSourceAge(ticker)?.bookAgeMs ?? null;

  let label: string;
  let title: string;
  let box: string;
  let dot: string;
  if (paused) {
    label = 'paused';
    title = 'Hydra throttled this connection (backpressure ≥ 2) — orderbook frames suspended';
    box = 'bg-amber-500/15 text-amber-400';
    dot = 'bg-amber-400 animate-pulse';
  } else if (ageMs == null) {
    label = 'Book ?';
    title = 'Book observation age unavailable; recent delivery does not establish freshness';
    box = 'bg-muted/40 text-muted-foreground';
    dot = 'bg-muted-foreground';
  } else {
    const secs = Math.floor(ageMs / 1000);
    label = `Book ${secs < 60 ? `${secs}s` : secs < 3600 ? `${Math.floor(secs / 60)}m` : secs < 86400 ? `${Math.floor(secs / 3600)}h` : `${Math.floor(secs / 86400)}d`}`;
    title = `${secs}s since the producer's last book observation, plus local elapsed time; upstream transit delay is unknown. Age alone does not prove the book is wrong.`;
    box = 'bg-muted/50 text-foreground/70';
    dot = 'bg-muted-foreground';
  }

  return (
    <span
      className={`inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-medium normal-case tabular-nums ${box}`}
      title={title}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />
      {label}
    </span>
  );
}
